import type { PAGE_POSITIONS } from 'types';
import type { AffiliateStockListing, CapiProductPage } from 'types/capi/pdp';
import type { StockStatus as StockStatusType } from 'types/capi/shared';

import ChevronRightStretched from '~/assets/icons/ChevronRightStretch';
import AffiliateProductLink from '~/components/AffiliateProductLink';
import DiscountCode from '~/components/DiscountCode';
import KlarnaPlacement from '~/components/KlarnaPlacement';
import { getStockStatusConfig } from '~/domain/stockStatus';
import usePageData from '~/hooks/usePageData';
import { formatReleaseDateLong } from '~/utils/dates';
import { getStockStatusForSize } from '~/utils/listings';

import AffiliateSizes from './AffiliateSizes';
import BuyNowButton from './BuyNowButton';
import ShippingAndPrice from './ShippingAndPrice';

type Props = {
  listing: AffiliateStockListing;
  pagePosition: PAGE_POSITIONS;
  position: number;
  selectedSize?: string;
};

export default function StockListing({
  listing,
  position,
  selectedSize,
  pagePosition
}: Props) {
  const product = usePageData<CapiProductPage>();

  const stockStatus = getStockStatusForSize(listing, selectedSize);

  return (
    <div className="flex size-full flex-col justify-center gap-4 rounded-lg bg-white p-4">
      <AffiliateProductLink
        price={listing.price}
        affiliate={listing.affiliate}
        product={product}
        buttonPosition={position}
        pagePosition={pagePosition}
        urlTemplates={listing}
        selectedSize={selectedSize}
        variant="none"
        className="flex flex-col justify-center gap-4"
      >
        <div className="flex w-full items-center gap-4 bg-white">
          <img
            src={listing.affiliate.logo}
            alt={listing.affiliate.name}
            className="h-9 w-16 bg-white object-cover"
          />
          <StockStatus
            stockStatus={stockStatus}
            listing={listing}
            selectedSize={selectedSize}
          />
          <div className="flex grow justify-end gap-4">
            <ShippingAndPrice listing={listing} selectedSize={selectedSize} />
            {stockStatus !== 'IN_STOCK' && (
              <div className="flex self-center justify-self-end">
                <ChevronRightStretched
                  height="32"
                  width="12"
                  stroke="#F2F2F2"
                  strokeWidth="2"
                />
              </div>
            )}
          </div>
        </div>
        {listing.affiliate.id === 'affiliate:marketplace' && (
          <KlarnaPlacement price={listing.price} />
        )}
        <AffiliateSizes selectedSize={selectedSize} sizes={listing.sizes} />
      </AffiliateProductLink>
      {(listing.discount_code?.code || stockStatus === 'IN_STOCK') && (
        <div className="flex w-full flex-col gap-2">
          <DiscountCode
            code={listing.discount_code?.code}
            amount={listing.discount_code?.amount}
          />
          {stockStatus === 'IN_STOCK' && (
            <BuyNowButton
              selectedSize={selectedSize}
              stockListing={listing}
              buttonPosition={position}
              pagePosition={pagePosition}
            />
          )}
        </div>
      )}
    </div>
  );
}

export function StockStatus({
  stockStatus,
  listing,
  selectedSize
}: {
  listing: AffiliateStockListing;
  selectedSize?: string;
  stockStatus: StockStatusType;
}) {
  const { textColourClass } = getStockStatusConfig(stockStatus)!;

  switch (stockStatus) {
    case 'IN_STOCK':
      return (
        <div className="flex flex-col justify-center">
          {listing.affiliate.is_reseller ? (
            <>
              <p className={`text-sm text-reseller-blue md:text-base`}>
                Marketplace
              </p>
              {listing.affiliate.id === 'affiliate:marketplace' && (
                <p className="text-xs text-black md:text-sm">Verified seller</p>
              )}
            </>
          ) : (
            <p className={`text-sm ${textColourClass} md:text-base`}>
              In stock
            </p>
          )}
        </div>
      );
    case 'COMING_SOON':
      return (
        <div className="flex flex-col text-sm md:text-base">
          <p className={`${textColourClass}`}>Coming Soon</p>
          <span>
            {listing.release_date
              ? formatReleaseDateLong(listing.release_date)
              : 'TBC'}
          </span>
        </div>
      );
    case 'SOLD_OUT':
      return (
        <div className={`text-sm ${textColourClass} md:text-base`}>
          {listing.sizes.length && selectedSize ? (
            <>
              <p className="text-nowrap">Out of stock</p>
              <p className="text-black">UK {selectedSize}</p>
            </>
          ) : (
            'Sold out'
          )}
        </div>
      );
    case 'TBC':
    default:
      return <p className={textColourClass}>TBC</p>;
  }
}
